import type { ServiceLineId } from './services'

export interface ArticleSection {
  heading?: string
  paragraphs: string[]
}

export interface Article {
  slug: string
  title: string
  excerpt: string
  summaryBlock: string
  serviceLine: ServiceLineId
  sectors: string[]
  author: { name: string; role: string }
  heroImage: string
  heroAlt: string
  readTime: number
  publishedAt: string
  gated: boolean
  teaserCut: number
  gateHeadline: string
  gateValueLine: string
  featured?: boolean
  body: ArticleSection[]
}

export const ARTICLES: Article[] = [
  {
    slug: 'africa-ai-market-2030',
    title: 'The 16.5 billion dollar question: who captures Africa AI market by 2030',
    excerpt:
      'Africa AI market is forecast to reach 16.5 billion dollars by 2030. The organisations that capture it will not be the ones that bought the most technology.',
    summaryBlock:
      'Africa AI market is projected at 16.5 billion dollars by 2030. Growth is concentrated in banking, health and government. The binding constraint is not model access, it is data foundations and governed adoption. Organisations that fix those two things first capture disproportionate value.',
    serviceLine: 'intelligence',
    sectors: ['Banking', 'Government', 'Health'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/699e5a7a-400b-41de-b35f-6923b525b29a.jpg',
    heroAlt: 'Abstract navy and gold artwork representing market intelligence data across Africa',
    readTime: 8,
    publishedAt: '2026-07-14',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine:
      'Read the full analysis, including the sector by sector breakdown and the three constraints we see most often. We will also send you our fortnightly brief.',
    featured: true,
    body: [
      {
        paragraphs: [
          'Africa AI market is forecast to reach 16.5 billion dollars by 2030. The figure is quoted often and understood rarely. It is not a pool of money waiting to be collected. It is the value that will be created by organisations that solve real problems with these tools, and most of that value will be captured by a small number of them.',
          'We have spent the past two years watching institutions across banking, government, health and agriculture approach this moment. The pattern is consistent enough to be worth writing down.',
        ],
      },
      {
        heading: 'Technology is not the constraint',
        paragraphs: [
          'Access to capable models is now close to free at the point of use. An organisation in Accra and one in Frankfurt can call the same model at the same price within the same second. Whatever the constraint on adoption is, it is not access.',
          'The constraint is what surrounds the model. Clean data with agreed definitions. A named owner for each decision the system touches. A review step where a person can disagree with the output and be heard. Training that reaches the people who will actually use the thing.',
        ],
      },
      {
        heading: 'Where the value concentrates',
        paragraphs: [
          'Banking will take the largest share, because the use cases are dense, the data is already structured and the return is measurable within a quarter. Credit memo preparation, fraud detection and portfolio decisioning are all sitting there.',
          'Health follows, though the shape is different. The near term spend goes on records and interoperability, which is unglamorous groundwork. The value arrives later, when forecasting capacity and supply becomes possible.',
          'Government is the largest opportunity and the slowest mover. Procurement cycles and legitimate caution both slow adoption, but the institutions that move now are setting standards the rest of the market will have to meet.',
        ],
      },
      {
        heading: 'What we advise',
        paragraphs: [
          'Start with two use cases, not twenty. Choose them because they change a number your board already watches, not because they demonstrate the technology well.',
          'Fix the data underneath those two use cases properly, and accept that this is most of the work. Write the governance before the build, not after the first incident.',
          'Then measure. If the number has not moved within two quarters, stop and choose differently. Most disappointment with AI comes from organisations that never defined what success would look like.',
        ],
      },
    ],
  },
  {
    slug: 'ai-readiness-five-dimensions',
    title: 'Five dimensions that decide whether your AI programme survives contact with reality',
    excerpt:
      'Most AI programmes fail on governance and skills, not on models. Here is the assessment we run before we agree to build anything.',
    summaryBlock:
      'AI readiness rests on five dimensions: data, skills, use cases, governance and budget. Weakness in any one caps the value of the other four. We score each out of twenty and start work on the lowest.',
    serviceLine: 'ai',
    sectors: ['Government', 'Banking', 'Education'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/f2429ec4-9f40-4646-840d-0c23f17cf8d0.jpg',
    heroAlt: 'Abstract navy artwork with gold arcs and nodes representing organisational AI adoption',
    readTime: 6,
    publishedAt: '2026-06-28',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the full five dimension breakdown and the scoring bands we use with clients.',
    body: [
      {
        paragraphs: [
          'Before we agree to build anything, we run the same assessment. It takes about ninety minutes with the right people in the room, and it saves months.',
          'The assessment covers five dimensions. Weakness in any one of them caps the value of the other four, which is why a strong data function alone predicts very little about whether a programme will work.',
        ],
      },
      {
        heading: 'One, data',
        paragraphs: [
          'Not how much you have. Whether the definitions are agreed, whether the records are current, and whether anybody can get to them without raising a ticket. Organisations consistently overrate themselves here.',
        ],
      },
      {
        heading: 'Two, skills',
        paragraphs: [
          'The question is not whether you employ data scientists. It is whether the people who will use the output can read it critically and push back on it. That capability is more scarce and more valuable.',
        ],
      },
      {
        heading: 'Three, use cases',
        paragraphs: [
          'A good use case changes a number the board already watches. If you cannot name that number, you do not yet have a use case, you have an interest.',
        ],
      },
      {
        heading: 'Four, governance',
        paragraphs: [
          'Who reviews the output, who is accountable when it is wrong, and what happens to a decision that a person disagrees with. Write this before the build.',
        ],
      },
      {
        heading: 'Five, budget',
        paragraphs: [
          'Programmes fail on running cost far more often than on build cost. Budget for two years of operation, monitoring and retraining, or do not start.',
        ],
      },
    ],
  },
  {
    slug: 'go-to-market-african-markets',
    title: 'Why go-to-market plans built for one African market rarely travel to the next',
    excerpt:
      'Channel economics, buyer behaviour and payment terms differ sharply between neighbouring markets. The plan has to be rebuilt, not translated.',
    summaryBlock:
      'Go-to-market plans rarely transfer between African markets because channel economics, procurement norms and payment terms differ sharply. Rebuild the channel and pricing layers per market, keep positioning and offer architecture consistent.',
    serviceLine: 'growth',
    sectors: ['SMEs', 'Logistics', 'Telecoms'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/8a2ab498-4d6e-4c00-b5a1-7944b119629a.jpg',
    heroAlt: 'Flat geometric illustration of stepped navy blocks with gold directional lines representing growth',
    readTime: 7,
    publishedAt: '2026-06-09',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the full breakdown, including the four layers we rebuild for every new market.',
    body: [
      {
        paragraphs: [
          'A plan that works in Lagos is frequently assumed to work in Nairobi with a currency change and a new distributor. It seldom does, and the reason is structural rather than cultural.',
          'We separate a go-to-market plan into four layers. Two of them travel well. Two of them almost never do.',
        ],
      },
      {
        heading: 'The layers that travel',
        paragraphs: [
          'Positioning travels. The problem you solve and the reason a buyer should believe you are usually stable across markets within the same sector.',
          'Offer architecture travels. How you package the work, what sits in each tier and where the upgrade path runs can normally be held constant.',
        ],
      },
      {
        heading: 'The layers that do not',
        paragraphs: [
          'Channel economics do not travel. The cost of reaching a buyer, the margin a partner expects and the length of the sales cycle vary widely between neighbouring markets, often by a factor of three.',
          'Payment and procurement terms do not travel. Public sector payment cycles, foreign exchange exposure and credit norms will reshape your cash position and therefore your entire plan.',
        ],
      },
      {
        heading: 'What we do instead',
        paragraphs: [
          'We hold positioning and offer constant, then rebuild channel and commercial terms per market from primary evidence. It costs more at the outset than translating a plan and considerably less than discovering the difference in month nine.',
        ],
      },
    ],
  },
  {
    slug: 'data-foundations-before-ai',
    title: 'The unglamorous work that decides whether AI pays for itself',
    excerpt:
      'Data definitions, ownership and quality checks are where most of the return sits. It is also the work that gets cut first.',
    summaryBlock:
      'Data foundations, meaning agreed definitions, named ownership and automated quality checks, account for most of the realised return on AI programmes. They are also the first item cut when budgets tighten.',
    serviceLine: 'ai',
    sectors: ['Health', 'Agriculture', 'Energy'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/f2429ec4-9f40-4646-840d-0c23f17cf8d0.jpg',
    heroAlt: 'Abstract navy artwork with gold arcs representing data foundations',
    readTime: 5,
    publishedAt: '2026-05-21',
    gated: false,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the rest of this analysis.',
    body: [
      {
        paragraphs: [
          'There is a version of every AI proposal where the data work is described as a prerequisite and then quietly reduced when the budget is reviewed. We have watched this happen enough times to treat it as the default risk.',
          'The work is genuinely unglamorous. It is agreeing what a customer is. It is deciding which system holds the authoritative record. It is writing a check that fires when a field starts arriving empty.',
        ],
      },
      {
        heading: 'Why it carries the return',
        paragraphs: [
          'A model applied to disputed data produces confident answers that different departments read differently. The output then requires a meeting to interpret, which is precisely the cost the programme was meant to remove.',
          'Fix the definitions and the same model produces an answer that ends the meeting instead of starting it. That is where the return lives.',
        ],
      },
      {
        heading: 'A practical sequence',
        paragraphs: [
          'Agree definitions for the twenty fields your two chosen use cases depend on. Name an owner for each. Automate a daily quality check on each. Then build.',
          'This is usually four to six weeks of work. It is the best value four to six weeks in the programme.',
        ],
      },
    ],
  },
  {
    slug: 'boards-asking-better-ai-questions',
    title: 'Six questions a board should ask before approving an AI budget',
    excerpt:
      'Boards are being asked to approve AI spending without the vocabulary to interrogate it. These six questions restore the balance.',
    summaryBlock:
      'Boards approving AI budgets should ask six questions: which number moves, what the running cost is, who reviews outputs, what the data dependency is, what happens if it is wrong, and how success will be measured.',
    serviceLine: 'ai',
    sectors: ['Government', 'NGOs', 'Banking'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/699e5a7a-400b-41de-b35f-6923b525b29a.jpg',
    heroAlt: 'Abstract navy and gold editorial artwork representing governance and oversight',
    readTime: 5,
    publishedAt: '2026-05-02',
    gated: true,
    teaserCut: 1,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read all six questions and the answers that should worry you.',
    body: [
      {
        paragraphs: [
          'Boards are routinely asked to approve AI spending by people who understand the technology far better than they do. That asymmetry is uncomfortable and it is also fixable. Six questions restore most of the balance.',
        ],
      },
      {
        heading: 'One, which number moves',
        paragraphs: [
          'Ask which figure in the management accounts changes if this succeeds, and by how much. A proposal that cannot answer this is a research project.',
        ],
      },
      {
        heading: 'Two, what does it cost to run',
        paragraphs: [
          'Build cost is quoted. Running cost is not. Ask for the two year operating cost including monitoring, retraining and support.',
        ],
      },
      {
        heading: 'Three, who reviews the output',
        paragraphs: [
          'Name the person and the frequency. If the answer is that the system is reviewed by the team that built it, the control is not real.',
        ],
      },
      {
        heading: 'Four, what is the data dependency',
        paragraphs: [
          'Ask which systems must stay accurate for this to keep working, and what happens when one of them changes.',
        ],
      },
      {
        heading: 'Five, what happens when it is wrong',
        paragraphs: [
          'Every system is wrong sometimes. Ask what the recovery process is and who the affected party can appeal to.',
        ],
      },
      {
        heading: 'Six, how will we know',
        paragraphs: [
          'Ask for the measurement plan and the date of the first review. Set the date in the minutes.',
        ],
      },
    ],
  },
  {
    slug: 'pricing-under-currency-pressure',
    title: 'Holding your pricing when the currency will not hold still',
    excerpt:
      'Currency movement erodes margin quietly. Three pricing structures we use with clients exposed to it.',
    summaryBlock:
      'Currency volatility erodes margin between quote and payment. Three structures help: indexed pricing, shortened payment terms with a discount, and split currency contracts for import heavy cost bases.',
    serviceLine: 'growth',
    sectors: ['SMEs', 'Logistics', 'Agriculture'],
    author: { name: 'Jonathan Clarke', role: 'Founder' },
    heroImage: 'https://cdn.magicpatterns.com/patterns/generated-images/8a2ab498-4d6e-4c00-b5a1-7944b119629a.jpg',
    heroAlt: 'Flat geometric illustration representing pricing structures',
    readTime: 6,
    publishedAt: '2026-04-16',
    gated: true,
    teaserCut: 2,
    gateHeadline: 'Continue reading',
    gateValueLine: 'Read the three pricing structures in full, with the trade offs of each.',
    body: [
      {
        paragraphs: [
          'Margin is frequently lost between the quote and the payment rather than in the negotiation. Where a currency moves ten per cent across a ninety day payment term, a carefully argued price becomes a loss without anybody making a decision.',
          'Three structures help. None of them is free, and choosing between them depends on where your costs sit.',
        ],
      },
      {
        heading: 'Indexed pricing',
        paragraphs: [
          'Quote in local currency but index to a reference rate, with a stated band inside which the price holds. Buyers accept this more readily than expected when the band is generous and the reference is public.',
        ],
      },
      {
        heading: 'Shortened terms with a discount',
        paragraphs: [
          'Offer a discount for payment within fourteen days that is smaller than your expected currency exposure across ninety. This converts an uncertain loss into a known one.',
        ],
      },
      {
        heading: 'Split currency contracts',
        paragraphs: [
          'Where your cost base is import heavy, bill the import linked portion in the currency you buy in and the labour portion locally. It is more administration and it protects the part of the margin most at risk.',
        ],
      },
    ],
  },
]

export function getArticle(slug: string): Article | undefined {
  return ARTICLES.find((article) => article.slug === slug)
}
